// Type definitions
export type BBox = [number, number, number, number]; // west, south, east, north

/**
 * Pads a bounding box by a given factor.
 * @param bounds The original bounding box literal { north, south, east, west } or null.
 * @param factor The padding factor (e.g., 0.5 for 50% padding).
 * @returns The padded bounding box as [west, south, east, north] or null if input is null.
 */
export const padBounds = (
    bounds: google.maps.LatLngBoundsLiteral | null,
    factor: number
): BBox | null => {
    if (!bounds) {
        return null;
    }

    const { north, south, east, west } = bounds;

    // Calculate width and height, handling potential longitude wrapping
    let lngDiff = east - west;
    const latDiff = north - south;

    // Adjust longitude difference if it crosses the anti-meridian
    if (lngDiff < 0) {
        lngDiff += 360;
    }

    // Calculate padding
    const paddingLng = lngDiff * factor;
    const paddingLat = latDiff * factor;

    // Calculate new bounds with padding
    const newWest = west - paddingLng;
    let newSouth = south - paddingLat;
    const newEast = east + paddingLng;
    let newNorth = north + paddingLat;

    // Clamp latitude to valid range [-90, 90]
    newSouth = Math.max(-90, newSouth);
    newNorth = Math.min(90, newNorth);

    // Normalize longitude to be within [-180, 180] - simple approach for now
    // More robust handling might be needed for extreme padding factors or near poles/anti-meridian
    // Example: If newWest < -180, newWest += 360; If newEast > 180, newEast -= 360;
    // However, for typical map padding, this might suffice.

    // Ensure west is still west of east after potential normalization/wrapping
    // This basic padding doesn't fully handle wrapping complex cases, assumes factor is reasonable.

    return [newWest, newSouth, newEast, newNorth];
}; 